import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AutoCompleteComponent } from 'auto-complete-lib';
import { IAutoCompleteItem } from 'auto-complete-lib';
import { Namespace } from 'enums-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Observable, of, ReplaySubject } from 'rxjs';
import { DocumentTypeServiceFactory, FilterLabel } from './document-type-service.factory';
import { DocumentTypeService } from './document-type.service';

export interface IDocumentType {
  id: string | undefined;
  label: string | undefined;
  custom?: boolean;
}

@Component({
  selector: 'app-document-type-picker',
  template: ` <app-kp-autocomplete
    [filterFunction]="this.queryFn.bind(this)"
    [placeholder]="placeholderText"
    [focusInputOnLoad]="focusInputOnLoad"
    [position]="position"
    [embeddedDropDown]="false"
    [clearOnSelection]="clearOnSelection"
    [scrollOnOptionsChange]="scrollOnOptionsChange"
    [disabled]="disabled"
    [maxHeight]="maxHeight"
    [valid]="validSelection && valid"
    [selected]="selection?.label ?? ''"
    (selectedChange)="documentTypeSelected($event)"
    [inputWidth]="'369px'"
    [expandUp]=expandUp
  >
  </app-kp-autocomplete>`,
  imports: [AutoCompleteComponent],
})
export class DocumentTypePickerComponent implements OnInit {
  @Input() placeholderText?: string = undefined;
  @Input() namespace!: string | Namespace;
  @Input() filterLabel?: FilterLabel;
  @Input() position: 'absolute' | 'initial' | 'fixed' = 'absolute';
  @Input() autoSelectOnExactMatch = true;
  @Input() clearOnSelection = false;
  @Input() initialTypeId = '';
  @Input() maxHeight = '336px';
  @Input() valid = true;
  @Input() disabled = true;
  @Input() allowCustomInput = false;
  @Input() scrollOnOptionsChange = true;
  @Input() focusInputOnLoad = false;
  @Input() expandUp = false;

  @Input() set entityType(entityType: string[]) {
    this.loadDocumentTypes(entityType);
  }

  @Output() selected = new EventEmitter<IDocumentType>();
  @Output() searchTextChange = new EventEmitter<string>();

  @ViewChild(AutoCompleteComponent)
  autoCompleteComponent!: AutoCompleteComponent<any>;

  validSelection = true;
  selection: IAutoCompleteItem<IDocumentType> | undefined;

  private validDocumentTypes: IDocumentType[] = [];
  private initialised = false;

  private readySubject = new ReplaySubject<void>(1);
  private ready$ = this.readySubject.asObservable();

  private static INVALID_DOCUMENT_TYPE: IDocumentType = {
    id: undefined,
    label: undefined,
  };

  constructor(
    private documentTypeServiceProvider: DocumentTypeServiceFactory,
    private messageNotificationService: MessageNotificationService,
    private documentTypeService: DocumentTypeService
  ) {}

  ngOnInit() {
    const documentTypeService = this.documentTypeServiceProvider.getService(this.namespace);
    this.placeholderText = this.placeholderText ?? `Enter ${documentTypeService.getDocumentTypeLabel()} type...`;
  }

  queryFn(filterValue: string): Observable<IAutoCompleteItem<IDocumentType>[]> {
    this.searchTextChange.emit(filterValue);

    // local function so we're correctly bound to this object
    const setValidSelectionState = (state: any) => {
      setTimeout(() => {
        this.validSelection = state;
      }, 0); // wait for next render cycle as we're in the async callback
    };

    // if filterValue is empty string then all validDocumentTypes will be included
    const filteredDocumentTypes = this.validDocumentTypes
      .filter((documentType) => documentType.label?.toLowerCase().includes(filterValue.toLowerCase()))
      .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

    // transform the filtered document types into a format expected by the autocomplete component
    const autocompleteItems = filteredDocumentTypes.map((documentType) => ({
      label: documentType.label ?? '',
      data: documentType,
    }));

    if (filterValue.length === 0) {
      setValidSelectionState(true);
      this.selected.emit(undefined);
      return of(autocompleteItems);
    }

    // user has typed an exact match
    if (this.autoSelectOnExactMatch && this.isExactMatch(autocompleteItems, filterValue)) {
      setValidSelectionState(true);
      this.documentTypeSelected(autocompleteItems[0].data);
    } else {
      setValidSelectionState(false);
      this.documentTypeSelected(DocumentTypePickerComponent.INVALID_DOCUMENT_TYPE);
    }

    return of(autocompleteItems);
  }

  documentTypeSelected(documentType: IDocumentType): void {
    this.validSelection = true;
    if (!this.allowCustomInput) {
      this.selection = documentType ? { data: documentType, label: documentType.label ?? '' } : undefined;
    }
    this.selected.emit(documentType);
  }

  customEntry(customValue: string): void {
    this.validSelection = true;
    this.selected.emit({ id: customValue, label: customValue, custom: true });
  }

  observeReady(): Observable<void> {
    return this.ready$;
  }

  public getValidDocumentTypes(): IDocumentType[] {
    return this.validDocumentTypes;
  }

  addItem(documentType: IDocumentType): void {
    if (!this.validDocumentTypes.find((dt) => dt.id === documentType.id)) {
      this.validDocumentTypes.push(documentType);
      this.validDocumentTypes.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
    }
  }

  removeItem(id: string): void {
    this.validDocumentTypes = this.validDocumentTypes.filter((dt) => dt.id !== id);
  }

  private loadDocumentTypes(entityTypes: string[]): void {
    this.disabled = true;
    if (!entityTypes?.find((value) => value)?.length) {
      // no entity selected
      this.initialised = true;
      this.readySubject.next();
      this.readySubject.complete();
      return;
    }

    this.documentTypeService.getDocumentTypes(this.namespace, entityTypes, this.filterLabel).subscribe({
      next: (docTypes) => {
        this.loadedDocumentTypes(docTypes);
      },
      error: (error) => this.messageNotificationService.error(`Failed to load document types.`, error),
    });
  }

  private loadedDocumentTypes(docTypes: IDocumentType[]): void {
    this.validDocumentTypes = docTypes;
    this.disabled = false;

    if (!this.initialised) {
      const initialSelection = this.validDocumentTypes.find((dt) => dt.id === this.initialTypeId);
      if (initialSelection) {
        this.selection = {
          data: initialSelection,
          label: initialSelection.label ?? '',
        };
      }
      this.initialised = true;
      this.readySubject.next();
      this.readySubject.complete();
    } else {
      this.autoCompleteComponent.clearSelection();
    }
  }

  private isExactMatch(items: IAutoCompleteItem<IDocumentType>[], value: string): boolean {
    if (items.length !== 1) {
      return false;
    }

    return (items[0].data.label ?? '').toLowerCase() === value.toLowerCase();
  }
}
